import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import FilePreviewModal from '../shared/FilePreviewModal';
import { RECOMMENDATIONS, RECOMMENDATION_LABELS, INTERVIEW_TYPE_LABELS, SOURCE_LABELS } from '../../utils/talentStages';

// ─── Interview feedback ──────────────────────────────────────────────────────
// The one screen an interviewer needs. Reached from their Action or the invite
// email; being on the panel is the access, so there is no page grant and the
// API answers 404 to anyone who is not on the panel or running the vacancy.
// Deliberately narrow: the candidate, the role, the CV, and a recommendation
// with notes. The structured scorecard will slot in here when it is final.

const REC_STYLE = {
  proceed: { on: 'bg-green-600 text-white border-green-600', off: 'bg-white text-green-700 border-green-200 hover:bg-green-50' },
  hold:    { on: 'bg-amber-500 text-white border-amber-500', off: 'bg-white text-amber-700 border-amber-200 hover:bg-amber-50' },
  reject:  { on: 'bg-red-600 text-white border-red-600',     off: 'bg-white text-red-700 border-red-200 hover:bg-red-50' },
};
const REC_BADGE = { proceed: 'bg-green-50 text-green-700', hold: 'bg-amber-50 text-amber-700', reject: 'bg-red-50 text-red-700' };

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })} at ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

export default function InterviewFeedbackPage({ interviewId }) {
  const { navigate, currentUser } = useApp();
  const { toast } = useToast();
  const [iv, setIv] = useState(null);
  const [error, setError] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [formError, setFormError] = useState('');
  const [viewingCv, setViewingCv] = useState(false);

  const load = useCallback(async () => {
    if (!interviewId) { setError('No interview was given. Open this screen from your Action or the invitation email.'); return; }
    try {
      const d = await api.get(`talent/interviews/${interviewId}`);
      setIv(d);
      setRecommendation(d.mine?.recommendation || '');
      setNotes(d.mine?.feedback_notes || '');
      setError('');
    } catch (e) {
      setError(e.status === 404
        ? 'This interview is not one you are on the panel for, or it no longer exists.'
        : (e.message || 'Could not load the interview.'));
    }
  }, [interviewId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!recommendation) { setFormError('Choose a recommendation first.'); return; }
    setSaving(true); setFormError('');
    try {
      const d = await api.post(`talent/interviews/${interviewId}/feedback`, { recommendation, notes });
      setIv(d);
      setSaved(true);
      toast('Feedback recorded', 'success');
    } catch (e) {
      setFormError(e.message || 'Could not save your feedback.');
    } finally { setSaving(false); }
  };

  const canSeeCandidate = !!(iv && (iv.can_manage || iv.mine));

  return (
    <div className="fade-in max-w-2xl mx-auto">
      <div className="mb-3">
        <h2 className="text-base font-extrabold text-navy">Interview feedback</h2>
        <div className="text-[9px] text-gray-500">Your recommendation for a candidate you interviewed. Only HR and the hiring manager see it.</div>
      </div>

      {error && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs text-gray-700">
          {error}
          <div className="mt-2"><button className="text-gold hover:underline" onClick={() => navigate('my-tasks')}>Go to your Actions</button></div>
        </div>
      )}

      {iv && !error && (
        <div className="space-y-3">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="text-sm font-bold text-navy">{iv.candidate.first_name} {iv.candidate.last_name}</div>
                <div className="text-[11px] text-gray-500">{iv.vacancy?.reference} · {iv.vacancy?.title}{iv.vacancy?.hiring_manager_name ? ` · Hiring manager ${iv.vacancy.hiring_manager_name}` : ''}</div>
              </div>
              <div className="flex flex-wrap gap-1">
                <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-50 text-purple-700">{iv.type_label || INTERVIEW_TYPE_LABELS[iv.type] || iv.type}</span>
                {iv.status === 'cancelled' && <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-50 text-red-700">Cancelled</span>}
              </div>
            </div>
            <dl className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-1 text-[11px] mt-3">
              <dt className="text-gray-500">When</dt><dd className="text-gray-800">{fmtWhen(iv.scheduled_at)} ({iv.duration_minutes} minutes)</dd>
              <dt className="text-gray-500">Where</dt><dd className="text-gray-800">{iv.location || 'To be confirmed'}</dd>
              <dt className="text-gray-500">Panel</dt><dd className="text-gray-800">{iv.panel.map(p => p.person_name).filter(Boolean).join(', ')}</dd>
              <dt className="text-gray-500">Candidate source</dt><dd className="text-gray-800">{SOURCE_LABELS[iv.candidate.source] || iv.candidate.source || '—'}{iv.candidate.source_detail ? ` · ${iv.candidate.source_detail}` : ''}</dd>
              <dt className="text-gray-500">CV</dt>
              <dd>{iv.candidate.cv_url ? <button type="button" className="text-gold hover:underline text-left break-all" onClick={() => setViewingCv(true)}>{iv.candidate.cv_filename || 'View CV'}</button> : <span className="text-gray-400">none uploaded</span>}</dd>
            </dl>
            {iv.notes && (
              <div className="mt-3 text-[11px] text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                <div className="text-[9px] font-bold text-gray-500 uppercase mb-0.5">Brief for the panel</div>
                <div className="whitespace-pre-wrap">{iv.notes}</div>
              </div>
            )}
            {iv.vacancy?.description && (
              <details className="mt-3 text-[11px] text-gray-700">
                <summary className="cursor-pointer text-gold">About the role</summary>
                <div className="mt-1 whitespace-pre-wrap">{iv.vacancy.description}</div>
              </details>
            )}
          </div>

          {iv.mine ? (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-[10px] font-bold text-gray-500 uppercase mb-2">Your recommendation</div>
              {iv.status === 'cancelled' ? (
                <div className="text-xs text-gray-500">This interview was cancelled, so no feedback is needed.</div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2" data-tour="recommendation">
                    {RECOMMENDATIONS.map(r => (
                      <button key={r} type="button" onClick={() => { setRecommendation(r); setFormError(''); setSaved(false); }}
                        className={`px-4 py-2 text-xs font-semibold rounded border ${recommendation === r ? REC_STYLE[r].on : REC_STYLE[r].off}`}>
                        {RECOMMENDATION_LABELS[r]}
                      </button>
                    ))}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-1">
                    Proceed: take them to the next stage. Hold: undecided, or wait for the rest of the panel. Reject: not this role.
                  </div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mt-3 mb-0.5">Notes</label>
                  <textarea rows={5} value={notes} onChange={e => { setNotes(e.target.value); setSaved(false); }}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs bg-white text-gray-800"
                    placeholder="What stood out, what concerned you, anything the next interviewer should probe." />
                  {formError && <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{formError}</div>}
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <button onClick={submit} disabled={saving}
                      className="px-4 py-2 text-xs font-semibold rounded bg-navy text-white hover:opacity-90 disabled:opacity-50">
                      {saving ? 'Saving…' : iv.mine.submitted_at ? 'Update feedback' : 'Submit feedback'}
                    </button>
                    {iv.mine.submitted_at && !saved && <span className="text-[10px] text-gray-500">Submitted {new Date(iv.mine.submitted_at.replace(' ', 'T') + 'Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}. You can change it.</span>}
                    {saved && <span className="text-[10px] text-green-700">Recorded. Your Action is closed; you can leave this page.</span>}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs text-gray-600">
              You are not on this panel, so there is nothing for you to submit here.
            </div>
          )}

          {canSeeCandidate && iv.panel.some(p => 'recommendation' in p && p.person_id !== currentUser?.id) && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-[10px] font-bold text-gray-500 uppercase mb-2">The panel's recommendations</div>
              <ul className="space-y-1">
                {iv.panel.map(p => (
                  <li key={p.id} className="flex items-start justify-between gap-2 text-[11px] border-b border-dashed border-gray-100 last:border-0 pb-1">
                    <div>
                      <span className="font-semibold text-navy">{p.person_name}</span>
                      {p.feedback_notes && <div className="text-gray-600 whitespace-pre-wrap">{p.feedback_notes}</div>}
                    </div>
                    {p.recommendation
                      ? <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${REC_BADGE[p.recommendation]}`}>{RECOMMENDATION_LABELS[p.recommendation]}</span>
                      : <span className="text-[9px] text-gray-400">awaiting</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {iv.can_manage && (
            <div className="text-[10px] text-gray-500">
              <button className="text-gold hover:underline" onClick={() => navigate('talent-acquisition')}>Back to Talent Acquisition</button>
            </div>
          )}
        </div>
      )}
      {viewingCv && iv?.candidate?.cv_url && (
        <FilePreviewModal url={iv.candidate.cv_url} filename={iv.candidate.cv_filename}
          title={`CV: ${iv.candidate.first_name} ${iv.candidate.last_name}`} onClose={() => setViewingCv(false)} />
      )}
    </div>
  );
}
